<!-- Freeze Cycle Adjustment Details -->
@if (isLoading()) {
  <!-- Loading Skeleton -->
  <div class="surface-0 border-1 border-200 border-round-lg overflow-hidden">
    <div class="bg-primary-50 px-3 py-2 border-bottom-1 border-200">
      <p-skeleton width="10rem" height="1rem" />
    </div>
    <div class="p-3">
      <div class="flex align-items-center mb-2 gap-2">
        <p-skeleton shape="circle" width="1.25rem" height="1.25rem" />
        <p-skeleton width="8rem" height="0.875rem" />
      </div>
      <p-skeleton width="100%" height="4.5rem" styleClass="mb-2 border-round" />
      <p-skeleton width="100%" height="4.5rem" styleClass="mb-2 border-round" />
      <p-skeleton width="100%" height="4.5rem" styleClass="border-round" />
      <div class="flex align-items-center mb-2 gap-2 mt-3">
        <p-skeleton shape="circle" width="1.25rem" height="1.25rem" />
        <p-skeleton width="10rem" height="0.875rem" />
      </div>
      <p-skeleton width="100%" height="4.5rem" styleClass="mb-2 border-round" />
      <p-skeleton width="100%" height="4.5rem" styleClass="border-round" />
    </div>
  </div>
}

@if (!isLoading() && cycleInfo(); as cycleInfo) {
  <div class="surface-0 border-1 border-200 border-round-lg overflow-hidden">
    <!-- Header -->
    @if (showTitle()) {
      <div class="bg-primary-50 px-3 py-2 border-bottom-1 border-200">
        <div class="flex align-items-center">
          <i class="fa fa-calendar-alt text-primary mr-2"></i>
          <span class="font-semibold text-primary-900 text-sm">
            {{ 'FREEZE.FREEZE_SIDEBAR.FREEZE_CYCLE_INFO' | translate }}
          </span>
        </div>
      </div>
    }

    <div class="p-3">
      <!-- Freeze Billing Cycle -->
      @if (cycleInfo.freeze_billing_cycle && cycleInfo.freeze_billing_cycle.length > 0) {
        <div class="mb-3" data-testid="freeze-billing-cycle-inline">
          <div class="flex align-items-center mb-2">
            <i class="fa fa-pause-circle text-blue-600 mr-2 text-sm"></i>
            <span class="font-semibold text-900 text-sm">
              {{ 'FREEZE.FREEZE_SIDEBAR.AFFECTED_BILLING_CYCLES' | translate }}
            </span>
            <span class="ml-auto text-xs text-500 font-medium">
              {{ cycleInfo.freeze_billing_cycle.length }} {{ 'FREEZE.FREEZE_SIDEBAR.CYCLES' | translate }}
            </span>
          </div>

          @for (cycle of billingCyclesVisible(); track cycle; let i = $index) {
            <div class="surface-50 border-round p-3 mb-2" [attr.data-testid]="'freeze-billing-cycle-inline-' + i">
              <!-- Cycle Period and Freeze Days -->
              <div class="mb-2">
                <div class="flex justify-content-between mb-1">
                  <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.CYCLE_PERIOD' | translate }}:</span>
                  <span class="font-semibold text-900 text-xs">{{ cycle.cycle_start_date | datetimeformat:'MMM dd':false }} - {{ cycle.cycle_end_date | datetimeformat:'MMM dd, yyyy':false }}</span>
                </div>
                <div class="flex justify-content-between mb-1">
                  <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.FREEZE_DAYS_LABEL' | translate }}:</span>
                  <span class="font-semibold text-blue-600 text-xs">{{ cycle.freeze_days }}/{{ cycle.total_days_in_cycle }} {{ 'FREEZE.FREEZE_SIDEBAR.DAYS' | translate }}</span>
                </div>
              </div>

              <!-- Amount Information -->
              <div class="border-top-1 border-200 pt-2">
                <div class="flex justify-content-between mb-1">
                  <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.CYCLE_AMOUNT' | translate }}:</span>
                  <span class="font-semibold text-900 text-xs">{{ cycle.base_amount_in_minor_currency | currencyformat }}</span>
                </div>
                <div class="flex justify-content-between mb-1">
                  <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.FREEZE_DAYS_VALUE' | translate }}:</span>
                  <span class="font-semibold text-blue-600 text-xs">{{ cycle.pro_rata_adjustment_amount_in_minor_currency | currencyformat }}</span>
                </div>
              </div>
            </div>
          }

          <!-- Expand / Collapse toggle for billing cycles -->
          @if (billingCyclesHasMore()) {
            <button
              type="button"
              class="w-full flex align-items-center justify-content-center gap-2 py-2 px-3 border-1 border-200 border-round cursor-pointer surface-0 hover:surface-100 transition-colors transition-duration-150 text-xs font-semibold text-600"
              (click)="toggleBillingCycles()"
              [attr.aria-label]="billingCyclesExpanded() ? 'collapse-billing-cycles' : 'expand-billing-cycles'"
              [attr.data-testid]="billingCyclesExpanded() ? 'collapse-billing-cycles' : 'expand-billing-cycles'"
            >
              @if (billingCyclesExpanded()) {
                <i class="fa fa-chevron-up text-xs"></i>
                {{ 'FREEZE.FREEZE_SIDEBAR.SHOW_LESS' | translate }}
              } @else {
                <i class="fa fa-chevron-down text-xs"></i>
                +{{ billingCyclesHiddenCount() }} {{ 'FREEZE.FREEZE_SIDEBAR.MORE_CYCLES' | translate }}
              }
            </button>
          }
        </div>
      }

      <!-- Freeze Adjusted Cycle -->
      @if (cycleInfo.freeze_adjusted_cycle && cycleInfo.freeze_adjusted_cycle.length > 0) {
        <div class="mb-2" data-testid="freeze-adjusted-cycle-inline">
          <div class="flex align-items-center mb-2">
            <i class="fa fa-credit-card text-green-600 mr-2 text-sm"></i>
            <span class="font-semibold text-900 text-sm">
              {{ 'FREEZE.FREEZE_SIDEBAR.CREDIT_APPLIED_TO' | translate }}
            </span>
            <span class="ml-auto text-xs text-500 font-medium">
              {{ cycleInfo.freeze_adjusted_cycle.length }} {{ 'FREEZE.FREEZE_SIDEBAR.CYCLES' | translate }}
            </span>
          </div>

          @for (cycle of adjustedCyclesVisible(); track cycle; let i = $index) {
            <div class="surface-50 border-round p-3 mb-2" [attr.data-testid]="'freeze-adjusted-cycle-inline-' + i">
              <div class="flex justify-content-between mb-1">
                <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.CYCLE_PERIOD' | translate }}:</span>
                <span class="font-semibold text-900 text-xs">{{ cycle.cycle_start_date | datetimeformat:'MMM dd':false }} - {{ cycle.cycle_end_date | datetimeformat:'MMM dd, yyyy':false }}</span>
              </div>
              @if (cycle.used_days !== undefined && cycle.unused_days !== null) {
                <div class="flex justify-content-between mb-1">
                  <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.USABLE_DAYS' | translate }}:</span>
                  <span class="font-semibold text-900 text-xs">{{ cycle.unused_days }}</span>
                </div>
              }
              <div class="flex justify-content-between mb-1">
                  <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.CYCLE_AMOUNT' | translate }}:</span>
                  <span class="font-semibold text-900 text-xs">{{ cycle.base_amount_in_minor_currency| currencyformat}}</span>
              </div>
              <div class="flex justify-content-between mb-1">
                <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.CREDIT_APPLIED' | translate }}:</span>
                <span class="font-semibold text-green-600 text-xs">{{ cycle.adjustment_amount_in_minor_currency | currencyformat }}</span>
              </div>
              @if (cycle.total_amount_to_charge_in_minor_currency !== undefined && cycle.total_amount_to_charge_in_minor_currency !== null) {
                <div class="flex justify-content-between">
                  <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.TOTAL_AMOUNT_TO_CHARGE' | translate }}:</span>
                  <span class="font-bold text-primary text-xs">{{ cycle.total_amount_to_charge_in_minor_currency | currencyformat }}</span>
                </div>
              }
              @if (cycle.pending_credits_in_minor_currency !== undefined && cycle.pending_credits_in_minor_currency !== null) {
                <div class="flex justify-content-between mb-1">
                  <span class="text-600 text-xs">{{ 'FREEZE.FREEZE_SIDEBAR.PENDING_ADJUSTMENT' | translate }}:</span>
                  <span class="font-semibold text-green-600 text-xs">{{ cycle.pending_credits_in_minor_currency | currencyformat }}</span>
                </div>
              }
            </div>
          }

          <!-- Expand / Collapse toggle for adjusted cycles -->
          @if (adjustedCyclesHasMore()) {
            <button
              type="button"
              class="w-full flex align-items-center justify-content-center gap-2 py-2 px-3 border-1 border-200 border-round cursor-pointer surface-0 hover:surface-100 transition-colors transition-duration-150 text-xs font-semibold text-600"
              (click)="toggleAdjustedCycles()"
              [attr.aria-label]="adjustedCyclesExpanded() ? 'collapse-adjusted-cycles' : 'expand-adjusted-cycles'"
              [attr.data-testid]="adjustedCyclesExpanded() ? 'collapse-adjusted-cycles' : 'expand-adjusted-cycles'"
            >
              @if (adjustedCyclesExpanded()) {
                <i class="fa fa-chevron-up text-xs"></i>
                {{ 'FREEZE.FREEZE_SIDEBAR.SHOW_LESS' | translate }}
              } @else {
                <i class="fa fa-chevron-down text-xs"></i>
                +{{ adjustedCyclesHiddenCount() }} {{ 'FREEZE.FREEZE_SIDEBAR.MORE_CYCLES' | translate }}
              }
            </button>
          }
        </div>
      }

      <!-- Total Freeze Credits Summary -->
      @if (false && cycleInfo.total_freeze_credits_in_minor_currency !== undefined && cycleInfo.total_freeze_credits_in_minor_currency !== null) {
        <div class="surface-100 border-round p-3 mb-2" data-testid="total-freeze-credits-inline">
          <div class="flex justify-content-between align-items-center">
            <div class="flex align-items-center">
              <i class="fa fa-coins text-green-600 mr-2"></i>
              <span class="font-semibold text-700 text-sm">{{ 'FREEZE.FREEZE_SIDEBAR.TOTAL_FREEZE_CREDITS' | translate }}:</span>
            </div>
            <span class="font-bold text-green-600 text-base">{{ cycleInfo.total_freeze_credits_in_minor_currency | currencyformat }}</span>
          </div>
        </div>
      }

      <!-- Next Charge Date -->
      @if (cycleInfo.next_charge_date) {
        <div class="surface-100 border-round p-2 text-xs" data-testid="next-charge-date-inline">
          <div class="flex justify-content-between align-items-center">
            <div class="flex align-items-center">
              <i class="fa fa-calendar-check text-primary mr-2"></i>
              <span class="font-semibold text-700">{{ 'FREEZE.FREEZE_SIDEBAR.NEXT_CHARGE_DATE' | translate }}:</span>
            </div>
            <span class="font-bold text-primary">{{ cycleInfo.next_charge_date | datetimeformat:'MMM dd, yyyy' }}</span>
          </div>
        </div>
      }
    </div>
  </div>
}

@if (!isLoading() && !cycleInfo()) {
  <!-- No data message -->
  <div class="text-center text-500 text-sm py-4">
    {{ 'FREEZE.FREEZE_SIDEBAR.NO_CYCLE_INFO' | translate }}
  </div>
}
